import { OrderedMap } from 'immutable';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import GoogleMap from 'react-google-maps/lib/components/GoogleMap';
import Polyline from 'react-google-maps/lib/components/Polyline';
import { useDispatch, useSelector } from 'react-redux';

import { UserCan } from '../../../shared/constants';
import { iFullStoreState, Units } from '../../../shared/interfaces';
import { useRedux } from "../../../states/redux-state";
import { IPointsContainer } from '../../../stores/reducers/devicesTripsPoints';
import { recenterMap, getMapZoom as getMapZoomFromStore } from '../../../stores/reducers/gmap-reducers';
import { setReplayPoint } from '../../../stores/reducers/replay/AC';
import { Pois, shouldShowPoint } from '../../gmap/pois';
import { Reply } from '../../gmap/Reply';
import { simplifyPolylineByZoom } from '../../../shared/polyline-simplification';


type DeviceTripProps = {
    deviceId: string;
    tripsDetails: {tripId: string, deviceId: string, color: string}[];
    isReportTrip?: boolean;
    tripId: string;
    tripIdx: number;
    last?: boolean;
    color: string;
    units: Units;
    live?: boolean;
    highlightPointId?: string;
}

type ReplayRoutePoint = {
    lat: number;
    lng: number;
    pointId: string;
    isLast?: boolean;
};

type ReplayRouteDetail = {
    color: string;
    points: ReplayRoutePoint[];
    polylinePath: Array<google.maps.LatLng | google.maps.LatLngLiteral>;
};

const toGooglePath = (
    path: { lat: number; lng: number }[],
): Array<google.maps.LatLng | google.maps.LatLngLiteral> => {
    if (typeof google === 'undefined' || !google?.maps?.LatLng) {
        return path;
    }

    return path.map((point) => new google.maps.LatLng(point.lat, point.lng));
};

const DeviceTrip = ({deviceId, tripId, tripIdx, last = false, color, units, highlightPointId, live = false, tripsDetails, isReportTrip = false}: DeviceTripProps) => {
    const mapRef = useSelector<iFullStoreState, React.RefObject<GoogleMap> | null>(state => state.gmap.mapRef());
    const toggleRecenter = useRedux(s => s.gmap.toggleRecenter);
    const replayPath = useSelector<iFullStoreState, boolean>((s) => s.gmap.replayPath);
    const dispatch = useDispatch();
    const user = useRedux(s => s.auth.user.acl);
    const showPOI = useRedux(s => s.gmap.showPOI);
    const showDots = useRedux(s => s.gmap.showDots);
    const showReportLines = useRedux(s => s.gmap.showReportLines);

    const rangeEnd = useSelector<iFullStoreState, moment.Moment | undefined>(
        s => s.timeline.range?.endDate,
        (l, r) => l?.unix() === r?.unix(),
    );
    const showLiveTrail = useSelector<iFullStoreState>(
        state => state.general.showLiveTrial
    );
    const shouldRenderTripLines = isReportTrip ? showReportLines : showLiveTrail;

    const showRoute = useSelector<iFullStoreState, boolean>(
        (s) => (s.devicesTripsPoints.getIn([deviceId, 'reportTripsIds', tripId]) as boolean) || false
    );

    const isDeviceMarkerHidden = useSelector<iFullStoreState, boolean>(
        (s) => {
            const details = s.devicesData.devicesDetails.get(deviceId);
            return details?.isMarkerHidden === true || details?.isArchived === true;
        },
    );

    const arraysOfTripPoints = useSelector<iFullStoreState, IPointsContainer[]>(
        s => !shouldRenderTripLines ? [] : tripsDetails.map(tripItem => {
            const tripData = s.devicesTripsPoints.getIn([tripItem.deviceId, 'tripsPoints', tripItem.tripId]) as IPointsContainer | undefined;
            return tripData || OrderedMap<string, any>();
        }
    ));

    const pointsRow = useSelector<iFullStoreState, IPointsContainer>(
        s => s.devicesTripsPoints.getIn([deviceId, 'tripsPoints', tripId], OrderedMap()) as IPointsContainer,
    );

    const mapZoom = useSelector<iFullStoreState, number>(
        s => getMapZoomFromStore(s.gmap)
    );

    const deviceFreshestPingId = useSelector<iFullStoreState, string | undefined>(
        state => last ? state.devicesData.devicesLastPing.getIn([deviceId, 'pointId']) as string : undefined,
    );

    let points = pointsRow;
    // if (highlightPointId) {
    //     points = pointsRow.filter((_, pointId) => pointId === highlightPointId);
    // }
    const pointsArray = useMemo(() => points.valueSeq().toArray(), [points]);
    const lastPoint = pointsArray[pointsArray.length - 1];
    const locations = useMemo(() => (
        shouldRenderTripLines
            ? pointsArray.map((point) => point.coordinates.location)
            : []
    ), [pointsArray, shouldRenderTripLines]);

    const handleReplayPointUpdate = useCallback((point) => {
        if (point?.pointId) {
            dispatch(setReplayPoint({point}));
        }
    }, [dispatch]);

    const filteredPois = useMemo<IPointsContainer>(() => {
        if (!showDots || !showPOI) {
            return points.filter(shouldShowPoint({showDots, showPOI})) as IPointsContainer;
        }

        if (showDots && !user.can.includes(UserCan.HIDE_POINTS)) {
            return points.filter(point => !point.hidden) as IPointsContainer;
        }

        return points;
    }, [points, showDots, showPOI, user]);

    const routeDetails = useMemo<ReplayRouteDetail[]>(() => {
        if (!shouldRenderTripLines) return [];

        return arraysOfTripPoints.map((tripPoints, i) => {
            const tripPointsArray = tripPoints.valueSeq().toArray();
            const routePoints = tripPointsArray.map((point, pointIndex) => ({
                ...point.coordinates.location,
                pointId: point.pointId,
                ...(pointIndex === tripPointsArray.length - 1 ? { isLast: true } : {}),
            })) as ReplayRoutePoint[];

            return {
                color: tripsDetails[i]?.color,
                points: routePoints,
                polylinePath: toGooglePath(routePoints),
            };
        });
    }, [arraysOfTripPoints, shouldRenderTripLines, tripsDetails]);

    React.useEffect(() => {

        if (pointsArray.length > 0
                && (!rangeEnd || rangeEnd.isAfter(lastPoint.time))
        ) {
            recenterMap(mapRef, lastPoint.coordinates.location, true, toggleRecenter);
        }
    }, [pointsArray.length]);

    // Simplify polyline only for static (non-replay) paths and when there are many points
    // Memoized by zoom level and replayPath to avoid recalculating Douglas-Peucker on every render
    const displayLocations = useMemo(() => {
        const simplifiedLocations = (!replayPath && locations.length > 100)
            ? simplifyPolylineByZoom(locations, mapZoom)
            : locations;

        return toGooglePath(simplifiedLocations);
    }, [replayPath, locations, mapZoom]);

    if (!points.size) return null;

    const polylineOptions = {
        strokeWeight: 1.5,
        strokeColor: color,
        ...(live ? {} : polylineDashedProps),
    };

    if (live && rangeEnd) {
        if (rangeEnd.isBefore(pointsArray[0].time)) {
            return null;
        }
    }
    const pointsWithoutTripId = tripId === "pointsWithoutTripId";
    const firstVisiblePoint = filteredPois?.valueSeq?.()?.first?.();
    const hasPoints = !!firstVisiblePoint;

    return <>
        {!pointsWithoutTripId && shouldRenderTripLines && !replayPath && (
            <Polyline
                path={displayLocations}
                options={polylineOptions}
            />
        )}
        <Pois
            lastPointId={deviceFreshestPingId}
            points={pointsArray}
            color={color}
            units={units}
            tripIdx={tripIdx}
            tripId={tripId}
            isDeviceMarkerHidden={isDeviceMarkerHidden}
        />
        {shouldRenderTripLines && showRoute && hasPoints && <Reply
            deviceId={deviceId}
            location={locations}
            polylineOptions={polylineOptions}
            routeDetails={routeDetails}
            firstPoint={firstVisiblePoint}
            onReplayPointUpdate={handleReplayPointUpdate}
        />}
    </>;
};

export default React.memo(DeviceTrip);

const polylineDashedProps = {
    strokeOpacity: 0,
    icons: [{
        icon: {path: 'M 0,-3 0,3', strokeOpacity: 1, scale: 1.5},
        offset: '0',
        repeat: '12px',
    }],
};
